import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Chip,
  Button,
  Tooltip,
  Typography,
  IconButton
} from "@mui/material";

import {
  getAbsencePermissionsAdmin,
  saveNewAbsencePermissions,
  editAbsencePermissions
} from './logic';

import {
  Check,
  Remove,
  AddCircle,
  ZoomIn,
} from '@mui/icons-material';

import BackDrop from '../../shared/backdropDE';
import ErrorSnackbar from '../error-snackbar';
import DataTable from '../../shared/data-table';
import { ACTIONS } from '../../shared/constants';
import { getUser } from '../../shared/auth-service';
import AbsencePermissionsDetail from './employee-absence-permissions-detail';
import dayjs from "dayjs";

const EmployeeAbsencePermissionsAdmin = () => {
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [dataItem, setDataItem] = useState({});
  const [showModal, setShowModal] = useState(false);
  const companyId = user.countryCode.split('-')[0];

  useEffect(() => {
    async function fetchData() {
      if (loading) {
        try {
          const model = {
            CompanyId: Number(user.companyId),
            EmployeeId: user.employeeId,
            AirportId: user.airports.map(airport => airport.id)
          };
          const response = await getAbsencePermissionsAdmin(model);
          const filteredData = response.data.filter(item => item.isVerified === false);
          setData(filteredData);
          setLoading(false);
        } catch (error) {
          setError(error.message);
          setLoading(false);
          setData([]);
        }
      }
    }
    fetchData();
    return () => {};
  });

  function formatDate(date) {
    var d = new Date(date),
      month = '' + (d.getMonth() + 1),
      day = '' + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
  }

  const handleSave = async (send) => {
    send.startDate = formatDate(send.startDate);
    send.endDate = formatDate(send.endDate);
    setShowModal(false);
    const save = await saveNewAbsencePermissions(send);
    setLoading(save);
  };

  const handleEdit = async (edit) => {
    setShowModal(false);
    edit.isVerify = edit.isVerified;
    delete edit.isVerified;
    const edited = await editAbsencePermissions(edit);
    setLoading(edited);
  };

  const handleDataEdit = async (cell) => {
    cell.employeeId = cell.employee.employeeId;
    cell.airportId = cell.airport.airportId;
    cell.isNew = false;
    cell.isRead = true;
    setDataItem(cell);
    setShowModal(true);
  };

  const getRowId = (row) => row.employeeAbsencePermissionsId;

  const typeRequest = [
    { type: 'Ausencia', color: 'info' },
    { type: 'Permiso', color: 'warning' },
    { type: 'Cumpleaños', color: 'error' },
    { type: 'Día de la familia', color: 'success' }
  ];

  const columns = [
    { field: "Ver", headerName: "Ver", width: 60, align: 'center', sortable: false,
      renderCell: (cellValues) => {
        return (
          <Tooltip title="Ver detalle de solicitud">
            <IconButton
              color="success"
              onClick={(e) => { handleDataEdit(cellValues.row) }}
            >
              <ZoomIn />
            </IconButton>
          </Tooltip>
        );
      }
    },
    { field: 'requirementType', headerName: 'Solicitud', width: 120,
      renderCell: (cellValues) => {
        const { requirementType } = cellValues.row;
        const typeInfo = typeRequest[requirementType - 1];
        return (
          <Tooltip title={typeInfo.type} arrow>
            <Chip 
              label={typeInfo.type} 
              color={typeInfo.color} 
              variant="outlined" 
            />
          </Tooltip>
        );
      }
    },
    { field: 'employee.employeeId', headerName: 'Identificación', width: 110,
      valueGetter: (params) => params.row.employee.employeeId,
    },
    { field: 'employee.name', headerName: 'Nombre Completo', width: 350,
      valueGetter: (params) => params.row.employee.name,
    },
    { field: 'employeeArea', headerName: 'Área', minWidth: 100, flex: 0.5 },
    { field: 'airport.code', headerName: 'Base', minWidth: 30,
       valueGetter: (params) => params.row.airport.code,
    },
    { field: 'startDate', headerName: 'Inicio', width: 100, align: 'center',
      valueGetter: (params) => params.value.substring(0,10)
    },
    { field: 'endDate', headerName: 'Final', width: 100, align: 'center',
      valueGetter: (params) => params.value.substring(0,10)
    },
    {
      field: 'daysBetween',
      headerName: 'Días',
      width: 80,
      align: 'center',
      valueGetter: (params) => {
        const start = new Date(params.row.startDate);
        const end = new Date(params.row.endDate);
    
        if (!isNaN(start) && !isNaN(end)) {
          // Calcula diferencia sin tiempo (solo fechas)
          const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
          const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    
          const diff = Math.floor((endDateOnly.getTime() - startDateOnly.getTime()) / (1000 * 60 * 60 * 24));
          return  diff + 1 ;
        }
    
        return '';
      }
    },
    { field: 'justify', headerName: 'Justificación', width: 200, flex: 0.8,
      valueGetter: (params) => params.row.justify,
    },
    { field: 'createdDate', headerName: 'Creado', width: 150, align: 'center',
      valueGetter: (params) => params.value ? dayjs(params.value.substring(0,16)).format("YYYY-MM-DD HH:mm") : ''
    }
  ];

  if (loading) { return (<BackDrop />); }
  if (error) { return (<ErrorSnackbar message={error} />); }

  return (
    <>
      {showModal && <AbsencePermissionsDetail
        data={dataItem}
        handleClose={setShowModal}
        handleSave={handleSave}
        handleEdit={handleEdit}
      />}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography style={{ color: '#00B0EF', fontWeight: 'bold', fontSize: '24px' }}>
            Ausencias <span style={{ color: '#1A3072' }}>y Permisos</span>
          </Typography>
        </Grid>
        <Box sx={{ my: 2, ml: 2 }} />
        <DataTable 
          columns={columns} 
          rows={data} 
          getRowId={getRowId}
          sx={{
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold'
            }
          }}
        />
      </Grid>
    </>
  );
};

export default EmployeeAbsencePermissionsAdmin;
